import type { Pass } from '@/api';

export type PassExpiryDraft = {
  mode: 'duration' | 'absolute' | 'infinite';
  expiresAt: string;
  durationDays: number;
  durationHours: number;
  durationPreset?: '7' | '30' | '90' | '365' | 'custom';
};

export const PASS_DURATION_PRESETS = [
  { value: '7', label: '7 дней', days: 7 },
  { value: '30', label: '30 дней', days: 30 },
  { value: '90', label: '90 дней', days: 90 },
  { value: '365', label: '1 год', days: 365 },
] as const;

export function localDateTime(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function createPassExpiryDraft(now = new Date()): PassExpiryDraft {
  const end = new Date(now);
  end.setDate(end.getDate() + 30);
  end.setHours(23, 59, 0, 0);
  return { mode: 'duration', expiresAt: localDateTime(end), durationDays: 30, durationHours: 0, durationPreset: '30' };
}

export function parsePassExpiry(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const date = new Date(value);
  // Reject invalid dates (including February 30) instead of accepting Date's normalization.
  return Number.isFinite(date.getTime()) && localDateTime(date) === value ? date : null;
}

export function isPassExpiryValid(value: PassExpiryDraft, now = new Date()): boolean {
  if (value.mode === 'infinite') return true;
  if (value.mode === 'absolute') {
    const date = parsePassExpiry(value.expiresAt);
    return date !== null && date.getTime() > now.getTime();
  }
  return Number.isInteger(value.durationDays) && value.durationDays >= 0 && value.durationDays <= 3650
    && Number.isInteger(value.durationHours) && value.durationHours >= 0 && value.durationHours <= 23
    && value.durationDays + value.durationHours > 0;
}

export function toPassExpiryPayload(value: PassExpiryDraft):
  | { expiresAt: string | null }
  | { durationDays: number; durationHours: number } {
  if (!isPassExpiryValid(value)) throw new RangeError('Укажите корректный срок проходки');
  if (value.mode === 'infinite') return { expiresAt: null };
  if (value.mode === 'duration') return { durationDays: value.durationDays, durationHours: value.durationHours };
  return { expiresAt: parsePassExpiry(value.expiresAt)!.toISOString() };
}

export function previewPassExpiry(value: PassExpiryDraft, currentExpiresAt?: string, now = new Date()): Date | null {
  if (!isPassExpiryValid(value, now) || value.mode === 'infinite') return null;
  if (value.mode === 'absolute') return parsePassExpiry(value.expiresAt);
  const base = currentExpiresAt ? Math.max(now.getTime(), new Date(currentExpiresAt).getTime()) : now.getTime();
  return new Date(base + (value.durationDays * 86400 + value.durationHours * 3600) * 1000);
}

export function formatPassExpiry(date: Date): string {
  return date.toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatPassDuration(value: PassExpiryDraft): string {
  const parts = [];
  if (value.durationDays) parts.push(`${value.durationDays} дн.`);
  if (value.durationHours) parts.push(`${value.durationHours} ч.`);
  return parts.join(' ');
}

export type PassTarget =
  | { kind: 'none' }
  | { kind: 'ambiguous' }
  | { kind: 'extend' | 'infinite'; pass: Pass };

// Preview the same scope selection as the API. The server checks it again on submit.
export function resolvePassTarget(passes: Pass[], serverId: number | null, now = new Date()): PassTarget {
  const eligible = passes.filter(pass => !pass.revokedAt || pass.revokeReason === 'expired');
  let candidates = eligible.filter(pass => pass.serverId === serverId);
  if (!candidates.length && serverId !== null) candidates = eligible.filter(pass => pass.serverId === null);
  if (!candidates.length && serverId === null) {
    const scopes = new Set(eligible.map(pass => pass.serverId));
    if (scopes.size > 1) return { kind: 'ambiguous' };
    candidates = eligible;
  }
  if (!candidates.length) return { kind: 'none' };
  const active = candidates.filter(pass => !pass.revokedAt && (!pass.expiresAt || new Date(pass.expiresAt) > now));
  if (active.length) candidates = active;
  const infinite = candidates.find(pass => pass.expiresAt === null);
  if (infinite) return { kind: 'infinite', pass: infinite };
  const pass = [...candidates].sort((a, b) => new Date(b.expiresAt!).getTime() - new Date(a.expiresAt!).getTime() || b.id - a.id)[0]!;
  return { kind: 'extend', pass };
}
